import React, { useState } from 'react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Badge } from '../ui/Badge';
import { EmptyState } from '../ui/Feedback';
import { Plus, Pencil, Trash2, Search, ChevronLeft, ChevronRight } from 'lucide-react';

interface Column<T> {
  key: string;
  label: string;
  render?: (item: T) => React.ReactNode;
  className?: string;
}

interface MasterDataTableProps<T> {
  data: T[];
  columns: Column<T>[];
  onAdd?: () => void;
  onEdit?: (item: T) => void;
  onDelete?: (item: T) => void;
  searchPlaceholder?: string;
  searchKey?: string;
  pageSize?: number;
  emptyMessage?: string;
  emptyDescription?: string;
  idKey?: string;
}

export function MasterDataTable<T extends Record<string, unknown>>({
  data, columns, onAdd, onEdit, onDelete,
  searchPlaceholder = 'Search...', searchKey = 'name',
  pageSize = 10, emptyMessage = 'No records found', emptyDescription = 'Add a new record to get started.',
  idKey = 'id',
}: MasterDataTableProps<T>) {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const filtered = data.filter(item => {
    const val = String(item[searchKey] || '').toLowerCase();
    return val.includes(search.toLowerCase());
  });

  const totalPages = Math.ceil(filtered.length / pageSize);
  const paged = filtered.slice((page - 1) * pageSize, page * pageSize);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input
            type="text"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            placeholder={searchPlaceholder}
            className="w-full bg-slate-800 border border-slate-700 rounded-lg pl-9 pr-3 py-2 text-xs text-slate-100 placeholder-slate-500 focus:outline-none focus:border-sky-500"
          />
        </div>
        {onAdd && (
          <Button variant="primary" size="sm" onClick={onAdd} icon={<Plus className="w-3.5 h-3.5" />}>
            Add New
          </Button>
        )}
      </div>

      {paged.length === 0 ? (
        <EmptyState message={emptyMessage} description={emptyDescription} />
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-slate-800">
                  {columns.map(col => (
                    <th key={col.key} className={`text-left py-3 px-3 font-semibold text-slate-400 ${col.className || ''}`}>
                      {col.label}
                    </th>
                  ))}
                  {(onEdit || onDelete) && <th className="text-right py-3 px-3 font-semibold text-slate-400 w-24">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {paged.map((item, idx) => (
                  <tr key={String(item[idKey]) || idx} className="border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors">
                    {columns.map(col => (
                      <td key={col.key} className={`py-3 px-3 text-slate-200 ${col.className || ''}`}>
                        {col.render ? col.render(item) : String(item[col.key] || '')}
                      </td>
                    ))}
                    {(onEdit || onDelete) && (
                      <td className="py-3 px-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {onEdit && (
                            <button onClick={() => onEdit(item)} className="p-1.5 rounded-lg text-slate-400 hover:text-sky-400 hover:bg-sky-500/10 transition-colors">
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                          )}
                          {onDelete && (
                            <button onClick={() => onDelete(item)} className="p-1.5 rounded-lg text-slate-400 hover:text-rose-400 hover:bg-rose-500/10 transition-colors">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between text-xs text-slate-400">
              <span>Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, filtered.length)} of {filtered.length}</span>
              <div className="flex items-center gap-1">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="p-1.5 rounded-lg hover:bg-slate-800 disabled:opacity-30">
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="px-2">{page} / {totalPages}</span>
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="p-1.5 rounded-lg hover:bg-slate-800 disabled:opacity-30">
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
